<template>
  <div class="flex flex-column gap-3">
    <h3>Pay Codes</h3>
    <a-alert
      v-if="loadError"
      type="error"
      show-icon
      message="Pay codes couldn't be loaded. Close and reopen this shift to try again."
      data-testid="shift-view-pay-codes-error"
    />
    <a-alert
      v-else
      type="info"
      show-icon
      :message="payCodeMessage"
      data-testid="shift-view-pay-codes-summary"
    />
    <a-table
      v-if="!loadError && payCodes.length"
      :rowKey="record => record.id"
      :locale="{ emptyText: 'Pay Codes Not Found' }"
      size="small"
      :columns="columns"
      :data-source="payCodes"
      :pagination="false"
      :scroll="{ y: 300 }"
    >
      <span slot="attached" slot-scope="text, record">
        <a-tag class="m-0" color="#3b82f6" v-show="record.attached">Attached</a-tag>
      </span>
    </a-table>
  </div>
</template>

<script>
import { Alert, Table, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapState: shiftState } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-alert': Alert,
    'a-table': Table,
    'a-tag': Tag
  },
  props: {
    loadError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      columns: [
        {
          title: 'Name',
          dataIndex: 'name',
          sorter: (a, b) => (a.name == null ? -1 : b.name == null ? 1 : a.name.localeCompare(b.name))
        },
        {
          title: 'Description',
          dataIndex: 'description',
          sorter: (a, b) =>
            a.description == null ? -1 : b.description == null ? 1 : a.description.localeCompare(b.description)
        },
        {
          title: 'Type',
          dataIndex: 'type',
          width: '100px',
          sorter: (a, b) => (a.type == null ? -1 : b.type == null ? 1 : a.type.localeCompare(b.type))
        },
        {
          title: 'Value',
          dataIndex: 'value',
          width: '100px',
          customRender: (_, record) => {
            return record.type === 'total' ? `$${record.value}` : `${record.value} hours`
          },
          sorter: (a, b) => a.value - b.value
        },
        {
          title: 'Attached',
          dataIndex: 'attached',
          scopedSlots: { customRender: 'attached' },
          sorter: (a, b) => a.attached - b.attached
        }
      ]
    }
  },
  computed: {
    ...shiftState({
      payCodes: state => state.shiftPayCodes,
      associate: state => state.shift.associate
    }),
    payCodeMessage() {
      if (!this.payCodes.length) {
        const name = `${this.associate.first_name} ${this.associate.last_name}`.trim()
        return `No pay codes on this shift. ${name} is paid at their base rate for all hours worked.`
      }

      const label = this.payCodes.length === 1 ? 'pay code' : 'pay codes'
      return `This shift has ${this.payCodes.length} ${label} applied. They affect how these hours are recorded and paid.`
    }
  }
}
</script>

<style scoped>
::v-deep .ant-table-footer {
  padding: 8px;
}

::v-deep .ant-table-header {
  scrollbar-width: none;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  padding-right: 8px;
}
</style>
